import React, { useState } from 'react';

const EXPERIENCE_LEVELS = [
  { max: 2.5,  label: 'Microdose',    color: 'text-green-600',  tip: 'Subtle effect, great for first-timers.' },
  { max: 5,    label: 'Beginner',     color: 'text-green-500',  tip: 'Mild euphoria, ideal for low tolerance.' },
  { max: 10,   label: 'Moderate',     color: 'text-yellow-600', tip: 'Noticeable effect. Know your tolerance.' },
  { max: 25,   label: 'Experienced',  color: 'text-orange-500', tip: 'Strong effect. Not for new consumers.' },
  { max: Infinity, label: 'High Dose', color: 'text-red-600',   tip: '⚠️ Very potent. Consume with extreme care.' },
];

function classifyDose(mgPerServing) {
  return EXPERIENCE_LEVELS.find(l => mgPerServing <= l.max);
}

/**
 * Full dosing formula:
 *   total_thc_mg = grams × (thc% / 100) × 1000 × decarbEff × infusionEff
 *   mg_per_serving = total_thc_mg / servings
 */
function calculate({ thcPercent, grams, servings, decarbEff, infusionEff }) {
  const totalThc =
    parseFloat(grams) *
    (parseFloat(thcPercent) / 100) *
    1000 *
    parseFloat(decarbEff) *
    parseFloat(infusionEff);
  const perServing = totalThc / parseInt(servings, 10);
  return {
    totalThc: totalThc.toFixed(1),
    perServing: perServing.toFixed(1),
    level: classifyDose(perServing),
  };
}

export default function DosingCalculator() {
  const [form, setForm] = useState({
    thcPercent:  '',
    grams:       '',
    servings:    '',
    decarbEff:   '0.88',
    infusionEff: '0.70',
  });
  const [result, setResult] = useState(null);
  const [error, setError]   = useState('');

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleCalculate = () => {
    setError('');
    const { thcPercent, grams, servings } = form;
    if (!thcPercent || !grams || !servings) {
      setError('Please fill in THC %, grams, and servings.');
      return;
    }
    if (
      isNaN(parseFloat(thcPercent)) ||
      isNaN(parseFloat(grams)) ||
      isNaN(parseInt(servings, 10))
    ) {
      setError('Please enter valid numbers.');
      return;
    }
    setResult(calculate(form));
  };

  const inputClass =
    'w-full p-3 rounded-lg bg-white dark:bg-zinc-800 text-sage-900 dark:text-zinc-100 border border-sage-900 dark:border-zinc-600 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-sage-900 dark:focus:ring-zinc-500';

  return (
    <div className="bg-sage-100 dark:bg-zinc-900 text-sage-900 dark:text-zinc-100 p-6 rounded-xl border border-sage-900 dark:border-zinc-700 shadow-lg max-w-xl mx-auto space-y-5 mt-8">
      <h2 className="text-2xl font-extrabold">Dosing Calculator</h2>

      {/* Primary inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold mb-1">THC %</label>
          <input
            type="number"
            min="0" max="100" step="0.1"
            placeholder="e.g. 20"
            value={form.thcPercent}
            onChange={set('thcPercent')}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">Flower (grams)</label>
          <input
            type="number"
            min="0" step="0.1"
            placeholder="e.g. 3.5"
            value={form.grams}
            onChange={set('grams')}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">Servings</label>
          <input
            type="number"
            min="1" step="1"
            placeholder="e.g. 12"
            value={form.servings}
            onChange={set('servings')}
            className={inputClass}
          />
        </div>
      </div>

      {/* Efficiency sliders */}
      <details className="text-sm">
        <summary className="cursor-pointer font-semibold text-sage-900 dark:text-zinc-300 select-none">
          Advanced: Efficiency Settings
        </summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold mb-1">
              Decarb efficiency: {Math.round(parseFloat(form.decarbEff) * 100)}%
            </label>
            <input
              type="range" min="0.5" max="1.0" step="0.01"
              value={form.decarbEff}
              onChange={set('decarbEff')}
              className="w-full accent-sage-900"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Default 88% — oven decarb at 240°F/40 min
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">
              Infusion efficiency: {Math.round(parseFloat(form.infusionEff) * 100)}%
            </label>
            <input
              type="range" min="0.4" max="1.0" step="0.01"
              value={form.infusionEff}
              onChange={set('infusionEff')}
              className="w-full accent-sage-900"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Butter ~60–80%, oil ~70–90%, tincture ~90%
            </p>
          </div>
        </div>
      </details>

      {error && <p className="text-red-600 text-sm font-semibold">{error}</p>}

      <button
        onClick={handleCalculate}
        className="w-full bg-sage-900 text-white font-bold py-3 rounded-full hover:opacity-90 transition"
      >
        Calculate Dose
      </button>

      {result && (
        <div className="mt-2 bg-white dark:bg-zinc-800 border border-sage-900 dark:border-zinc-700 rounded-xl p-5 space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wide">Total THC</p>
              <p className="text-2xl font-extrabold">{result.totalThc} mg</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-500 uppercase tracking-wide">Per Serving</p>
              <p className="text-2xl font-extrabold">{result.perServing} mg</p>
            </div>
          </div>

          <div className={`font-bold text-lg ${result.level.color}`}>
            {result.level.label}
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{result.level.tip}</p>

          <p className="text-xs text-zinc-400 dark:text-zinc-500 border-t border-zinc-200 dark:border-zinc-700 pt-3">
            Onset: 45–90 min after consumption. Effects can last 4–8 hours.
            Start low, go slow. This calculator is for educational purposes only.
          </p>
        </div>
      )}
    </div>
  );
}
